import React, { useEffect, useState, memo } from 'react'
import styled from 'styled-components'
import jstz from 'jstz'

import { Header } from '../../../lib/header'
import useDefer from '../../../common/hooks/useDefer'
import {
    LatestChargingSessionsUser,
    CHARGING_SESSIONS_USER_NO_OF_ITEMS,
} from '../../../lib/latest-charging-sessions-user'
import { ChargingStatistics } from '../../../lib/charging-statistics'
import loadChargerUsage from '../../../lib/loadChargeUsage'
import { DoughnutChart } from '../../../lib/doughnut-chart'
import Text from '../../../ui/Text'
import { UserAppStatisticsTotalConsumptionWidget } from '../../components/user-app-statistics-total-consumption-widget'
import loadReportForUser from '../../../lib/loadReportForUser'
import { MeasurementDeviceProperties } from '../../../common/features/measurement-device/measurement-device-properties'
import { LoadBalancerStatistics } from '../../../lib/load-balancer-statistics'
import { WidgetLoadingState } from '../../../lib/widget-loading-state'
import {
    useStatisticsGetUserTotalConsumptionQuery,
    useStatisticsGetUserUsageStatisticsQuery,
    useChargingSessionGetChargingSessionsForCurrentUserQuery,
    IDetailedChargingSession,
    useMeasurementDevicesGetDevicesByUserIdQuery,
} from '../../../common/redux/api/web-api'
import useText from '../../../common/hooks/useText'
import { useSessionUser } from '../../../common/hooks/useSessionUser'

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 56px;
`

export type UserAppStatisticPageProps = {
    style?: React.CSSProperties
}

export const UserAppStatisticPage = memo(function UserAppStatisticPage({
    style,
}: UserAppStatisticPageProps): React.ReactElement {
    const text = useText()
    const user = useSessionUser()
    const userId = user.id
    const [chargingSessionsOffset, setChargingSessionsOffset] = useState(0)
    const [charginsSessions, setChargingSessions] = useState<Array<IDetailedChargingSession>>([])
    const [selectedInterval, setSelectedInterval] = useState('currentmonth')
    const [loadingDownload, setLoadingDownload] = useState(false)

    /** Get charging sessions **/
    const { data: chargingSessionsData, isFetching: loadingChargingSessions } =
        useChargingSessionGetChargingSessionsForCurrentUserQuery({
            offset: chargingSessionsOffset,
            noOfItems: CHARGING_SESSIONS_USER_NO_OF_ITEMS,
        })

    useEffect(() => {
        if (chargingSessionsData) {
            setChargingSessions((currentSessions) =>
                currentSessions.length ? currentSessions.concat(chargingSessionsData) : chargingSessionsData
            )
        }
    }, [chargingSessionsData])

    const loadMoreSessions = () => {
        setChargingSessionsOffset((currentOffset) => currentOffset + CHARGING_SESSIONS_USER_NO_OF_ITEMS)
    }
    /***************************/

    /** Get user statistics **/
    const { data: userStatistic } = useStatisticsGetUserUsageStatisticsQuery({ userId }, { skip: !userId })
    /************************/

    /** Get charger usage **/
    const getChargerUsage = useDefer(
        (userId?: string) => {
            if (userId) return loadChargerUsage(userId)
        },
        [],
        [user?.id]
    )
    /***********************/

    /** get user total consumption **/
    const { data: totalConsumtionData, isLoading: loadingConsumption } = useStatisticsGetUserTotalConsumptionQuery(
        {
            userId,
            interval: selectedInterval,
        },
        { skip: !userId }
    )
    /********************************/

    /** Download data **/
    const downloadData = () => {
        if (!userId) return
        setLoadingDownload(true)

        const tz = jstz.determine()
        const timeZone = tz.name()
        let fileName = ''

        switch (selectedInterval) {
            case 'currentmonth':
                fileName = text('msgThisMonthOption')
                break
            case 'lastmonth':
                fileName = text('msgLastMonthOption')
                break
            case 'currentyear':
                fileName = text('msgThisYearOption')
                break
            default:
                fileName = text('msgLastYearOption')
                break
        }

        loadReportForUser(100, user.id, selectedInterval, timeZone).then(
            (report) => {
                let csvData = '\ufeff' + report.formattedData
                if (!report.formattedData || report.formattedData.length === 0) {
                    csvData = '\ufeff' + 'N/A'
                }
                const file = new Blob([csvData], { type: 'application/txt' })
                const anchor = Object.assign(document.createElement('a'), {
                    href: URL.createObjectURL(file),
                    target: '_blank',
                    download: fileName + '.csv',
                    style: 'display: none',
                })
                document.body.appendChild(anchor)
                anchor.click()
                anchor.remove()
                setLoadingDownload(false)
            },
            () => {
                console.error('could not load report data')
            }
        )
    }
    /*******************/

    const { data: measurementDevices = [], isFetching } = useMeasurementDevicesGetDevicesByUserIdQuery(
        { userId },
        { skip: !userId }
    )

    return (
        <div className="page-layout-container" style={style}>
            <div className="margin-bottom-small">
                <Header code="lblStatistics" size="lg" data-testid="header" />
            </div>
            <div className="row margin-bottom">
                <Container className="col-xs-12 col-md-8">
                    <UserAppStatisticsTotalConsumptionWidget
                        download={() => downloadData()}
                        downloadLoading={loadingDownload}
                        reloadChart={(type) => {
                            setSelectedInterval(type)
                        }}
                        data={totalConsumtionData ?? []}
                        useMonth={selectedInterval === 'currentyear' || selectedInterval === 'lastyear'}
                    />
                    {loadingConsumption && <WidgetLoadingState height="360px" />}
                    <LatestChargingSessionsUser
                        sessions={charginsSessions}
                        showEndTime
                        loadMoreChargingSessions={() => loadMoreSessions()}
                        loading={loadingChargingSessions}
                    />
                </Container>
                <div className="col-xs-12 col-md-4 margin-top-small">
                    <div className="row text-center">
                        <span className="dashboard-graph">
                            <Text code="lblMostFrequentChargers"></Text>
                        </span>
                        <DoughnutChart data={getChargerUsage.value ?? []} />
                    </div>
                    <ChargingStatistics stats={userStatistic} direction="column"></ChargingStatistics>
                </div>
            </div>
            <div className="row">
                <div className="col-xs-12 col-md-6">
                    {isFetching ? (
                        <WidgetLoadingState height="200px" />
                    ) : (
                        <MeasurementDeviceProperties devices={measurementDevices} />
                    )}
                </div>
            </div>
            <LoadBalancerStatistics device={measurementDevices?.[0]} />
        </div>
    )
})
